import { Account } from './account';
import { Address } from './address';
import { AddressFactors } from './address-factors';
import { AppointmentCategoryEnum } from './appointment';
import { DataItem, InfoModify } from './common';
import { QuoteCustomInventoryItem, QuoteInventoryItem } from './quote-inventory-item';
import { QuoteLog } from './quote-log';

export enum QuoteAccessTypeEnum {
  FrontDoor = 1,
  Stairs = 2,
  Elevator = 3,
}

export enum QuoteTruckParkingEnum {
  Unknown = 0,
  Driveway = 1,
  LoadingDock = 2,
  OnTheStreet = 3,
  ParkingLot = 4,
}

export enum QuoteTypeOfDwellingEnum {
  Unknown = 0,
  House = 1,
  Apartment = 2,
  Condo = 3,
  Townhome = 4,
  StorageUnit = 5,
  Business = 6,
}

export enum QuoteLongWalksEnum {
  LessThen50 = 0,
  Between50And100 = 1,
  Between100And150 = 2,
  Between150And200 = 3,
  Between200And250 = 4,
  Between250And300 = 5,
  Between300And350 = 6,
}

export enum QuoteSpaceTypeEnum {
  Custom = 1,
  Kitchen = 2,
  Bedroom = 4,
  Garage = 5,
  LivingRoom = 6,
  Basement = 7,
  Bathroom = 8,
  Attic = 9,
  FamilyRoom = 13,
  DiningRoom = 14,
  Gym = 15,
  OutdoorSpace = 16,
  Office = 17,
}

export enum QuoteSpaceOrderEnum {
  Default = 9999,
  Unassigned = 10000,
}

export enum QuoteWorkTypeIdEnum {
  LoadingAndUnLoading = 1,
  LoadingOnly = 2,
  UnLoadingOnly = 3,
  Packing = 4,
}

export enum QuoteFormModeEnum {
  StressFree = 1,
  Inventory = 2,
}

export enum QuotePricingTypeEnum {
  Hourly = 0,
  FlatRate = 1,
  AllInclusive = 2,
}

export enum QuoteTypeEnum {
  StressFree = 1,
  Inventory = 2,
}

export enum QuoteLaborRateType {
  labor = 'labor',
  junk = 'junk',
  custom = 'custom',
}

export type QuoteAccount = Pick<Account, 'id' | 'firstName' | 'lastName' | 'email' | 'phone'>;

export type QuoteRooms = '1' | '2' | '3' | '4' | '4+' | '';

export type QuoteAttachment = {
  id: number;
  url: string;
  description: string;
  isActive: number;
};

export type QuoteAddress = Address &
  Pick<AddressFactors, 'floor' | 'accessType' | 'longWalks'> & {
    truckParkingType: Pick<DataItem<AddressFactors['truckParking']>, 'id'>;
    dwellingType: Pick<DataItem<AddressFactors['typeOfDwelling']>, 'id'>;
    loadingDockReservationRequired: AddressFactors['loadingDock'];
    forBusiness?: boolean;
  };

export type QuoteSpace = {
  id: number;
  key: string;
  name: string;
  type: {
    id: number;
    name: string;
    iconUrl: string;
    order: number;
  };
};

export type QuotePricing = {
  adjustment: number;
  junkRemoval: number;
  type: QuotePricingTypeEnum;
  flatRate: number;
  discount: number;
  discountTruckAndTravel: number;
  hourlyRate: number;
};

export type QuoteNote = {
  size?: string;
  readiness?: string;
  logistical?: string;
  special?: string;
  junk?: string;
  generated?: string;
  moveFactors?: string;
  internalNotes?: string;
  internal?: string;
};

export type FullQuote = {
  id: number;
  account: QuoteAccount;
  numStops: number;
  squareFootage: number;
  numberOfBoxes: number;
  cioNeeded: boolean;
  moveReady: boolean;
  packingNeeded: boolean;
  rooms: QuoteRooms;
  hunks: number;
  date: string;
  parsedDate: string;
  appliances: number;
  assemblyItems: number;
  heavyItems: number;
  isWeekend: boolean;
  emailClient?: boolean;
  origin: QuoteAddress;
  stops: Address[];
  destination: QuoteAddress;
  pricing: QuotePricing;
  quoteLogs: QuoteLog[];
  attachments: QuoteAttachment[];
  inventory: QuoteInventoryItem[];
  spaces: QuoteSpace[];
  customItems: QuoteCustomInventoryItem[];
  location: {
    id: number;
  };
  estimate: {
    id: number;
  };
  job: {
    id: number;
  };
  category: {
    id: AppointmentCategoryEnum;
    name: string;
  };
  workType: {
    id: number;
  };
  type: {
    id: QuoteTypeEnum;
  };
  notes: QuoteNote;
  created: {
    by: number;
    datetime: string;
  };
  modified: {
    by: number;
    datetime: string;
  };
  extraLaborHours?: number;
  laborRate?: number;
};

export type Quote = {
  id: number;
  original: number;
  basedOn: number;
  version: number;
  label: string;
  numHunks: number;
  metUrl: string;
  category: DataItem;
  type: DataItem;
  notes: {
    moveFactors: string;
    specialInstructions: string;
  };
  created: InfoModify;
};
